import { nonceContext } from "@coreconsulting/react-router-lambda-adapter";
// Named imports: default-import interop for CJS deps differs between dev server and Lambda bundle, can leave `.default` undefined.
import { parse as parseAcceptLanguage } from "accept-language-parser";
import { parse as parseCookieHeader } from "cookie";
import Cookies from "js-cookie";
import * as React from "react";
import { IntlProvider } from "react-intl";
import {
  isRouteErrorResponse,
  Links,
  type LinksFunction,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  useRouteLoaderData,
} from "react-router";
import { ToastContainer } from "react-toastify";
import toastStylesHref from "react-toastify/dist/ReactToastify.css?url";
import type { Route } from "./+types/root";
import CoreLogo from "./components/core-logo";
import Header from "./components/header";
import { LanguageSelector } from "./components/layout";
import LoginImage from "./images/Login.png";
import { getMessages } from "./locales/messages";
import { Text } from "./locales/text";
import { LocaleProvider, languages } from "./locales/use-locale";
import { setClientLogLevel, toLogLevel } from "./logger.browser";
import globalStylesHref from "./styles/global.css?url";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: globalStylesHref },
  { rel: "stylesheet", href: toastStylesHref },
  // Architect deploys the public directory to /_static/
  { rel: "icon", href: "/_static/favicon.ico" },
];

export const meta: Route.MetaFunction = () => [{ title: "Unified identity - Coreconsulting" }];

export async function loader({ request, context }: Route.LoaderArgs) {
  const cookies = request.headers.get("Cookie") || "";
  const parsedCookies = parseCookieHeader(cookies);
  const acceptLanguages = parseAcceptLanguage(request.headers.get("Accept-Language") as string);
  const nonce = context.get(nonceContext) ?? "";

  // Runtime-read (never config.server.ts) so the client level isn't baked in at build time.
  const clientLogLevel = process.env.LOG_LEVEL_CLIENT || process.env.LOG_LEVEL || "warn";

  const preferredLang = parsedCookies.selectedLang;
  if (preferredLang) {
    return { language: preferredLang, nonce, clientLogLevel };
  }

  if (acceptLanguages?.length < 1) return { language: "en", nonce, clientLogLevel };

  const browserLanguage = acceptLanguages[0].code;
  const hasLang = languages.includes(browserLanguage);
  return { language: hasLang ? browserLanguage : "en", nonce, clientLogLevel };
}

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData<typeof loader>("root");
  const language = data?.language ?? "en";
  // Undefined (not "") when the root loader didn't run, so Links/ScrollRestoration/Scripts fall back to the adapter's real nonce instead of a literal empty one.
  const nonce = data?.nonce;

  // Safe to call on both SSR and hydration renders; only meaningful once the client logger is used in the browser.
  setClientLogLevel(toLogLevel(data?.clientLogLevel));

  return (
    <html lang={language} className="h-full">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        {/* Browsers hide the nonce attribute after insertion, so a real value here would always look like a hydration mismatch; pinned to "" since style-src is 'unsafe-inline' - must become the real nonce if that's ever tightened. */}
        <Links nonce="" />
      </head>
      <body className="h-full">
        <ToastContainer />
        {children}
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

// Renders for unmatched URLs before any loader runs, so it can't assume root loader data and can't reuse ~/components/layout's <Layout>; degrades to "en" server-side, upgrading client-side post-hydration to keep SSR and first render in sync.
export function ErrorBoundary() {
  const error = useRouteError();
  const isResp = isRouteErrorResponse(error);
  const isNotFound = isResp && error.status === 404;

  const rootData = useRouteLoaderData<typeof loader>("root");
  const [language, setLanguage] = React.useState<string>(rootData?.language ?? "en");

  React.useEffect(() => {
    if (rootData?.language) return;
    try {
      const browserLanguage = window.navigator.language?.slice(0, 2);
      if (browserLanguage && languages.includes(browserLanguage)) {
        setLanguage(browserLanguage);
      }
    } catch {
      // "en" from initial state stands.
    }
  }, [rootData?.language]);

  function onLanguageChange(selected: string) {
    try {
      Cookies.set("selectedLang", selected, { expires: 7 });
    } catch {
      // Cookie write can fail (e.g. storage disabled); in-memory selection still applies for this page view.
    }
    setLanguage(selected);
  }

  return (
    <LocaleProvider locale={language}>
      <IntlProvider locale={language} defaultLocale="en" messages={getMessages(language)}>
        <div>
          <Header
            className="basic-header"
            leftComponent={<div className="left" />}
            centerComponent={
              <div className="core-logo-container">
                <CoreLogo />
              </div>
            }
            rightComponent={
              <div className="right">
                <LanguageSelector onChange={onLanguageChange} value={language} values={languages} />
              </div>
            }
          />
          <main>
            <div className="main-content">
              <div className="left-content">
                <img src={LoginImage} alt="" className="company-image" />
              </div>
              <div className="right-content">
                <h2 className="title-sm">
                  <Text id={isNotFound ? "error-not-found-title" : "error-generic-title"} />
                </h2>
                <p className="text">
                  <Text id="error-guidance" />
                </p>
              </div>
            </div>
          </main>
        </div>
      </IntlProvider>
    </LocaleProvider>
  );
}
